import offlineDB, { invalidateBranchStockCaches } from '@/indexedDB/offlineDB.js';
import { runChangeFeedSync } from '@/services/changeFeedService.js';
import { runOfflineListSync } from '@/services/offlineSyncService.js';
import { reconcileSubscriptions, verifyOfflineCaches } from '@/services/subscriptionService.js';

/**
 * Converge this session's caches after a gap, then prove it worked.
 *
 * Three mechanisms, each covering what the one before it cannot:
 *
 * 1. The change feed replays exactly what moved since our cursor. Cheapest and
 *    most precise, but it can only replay what something remembered to log.
 * 2. The watermark scan re-reads each list on `updated_at >= watermark`. It
 *    depends on no event log at all, so it cannot miss a committed write —
 *    though it structurally cannot see a delete.
 * 3. Checksum verification asks whether the result actually matches the server.
 *    Only the subscriptions that disagree pay for a manifest, and that manifest
 *    is the one thing that *can* see a delete.
 *
 * This lives in its own module because it is the only place that knows about all
 * three, and because none of the three know about it — a coordinator that
 * imports downward has no cycle to dodge, where the same code inside
 * `offlineSyncService` needed dynamic imports to avoid closing one with
 * `changeFeedService`.
 */

/**
 * Longest a session may run on change-feed replay alone before a watermark scan
 * is forced anyway.
 *
 * This is the bound on how stale a cache can get when the feed loses a row, so
 * it is a correctness knob wearing a performance knob's clothes.
 *
 * It used to be fifteen minutes, chosen when this fan-out was the *only* thing
 * that ever refreshed a list cache. It is not any more. Three cheaper mechanisms
 * now sit in front of it — Reverb live, the change-feed replay that runs on
 * every convergence, and `ensureTableSynced` on the visit to the page that
 * actually reads a table — and each of them converges a table the operator can
 * see. What is left for the scan is the case none of the three cover: a table
 * nobody opened, whose feed row the best-effort log dropped. That is rare, and
 * nobody is looking at it, so paying thirteen paginated reads for it every
 * quarter of an hour bought staleness insurance on data no screen was showing.
 */
export const WATERMARK_FLOOR_MS = 6 * 60 * 60_000;

/**
 * The floor that applies when there is no replay to be a backstop *to*.
 *
 * `changeFeedSync` ships off, and its server half (`services.offline_sync
 * .change_feed.enabled`) ships off too — it is still a spike. So in every
 * default install `runChangeFeedSync` answers `{ok:false, reason:'flag_disabled'}`
 * and this scan is not the safety net, it is the only mechanism that repairs a
 * table nobody has opened.
 *
 * That deserves a shorter floor than six hours. What it does *not* deserve is
 * what it used to get: `!replayed?.ok` read a switched-off feature as a replay
 * that had just failed, so `scanIsDue` was unconditionally true and the fan-out
 * ran on every single convergence — every refresh, every visibility resume, every
 * reconnect. The throttle above was live code that no shipped configuration
 * could ever reach.
 */
export const DEGRADED_WATERMARK_FLOOR_MS = 30 * 60_000;

/**
 * Same idea for the checksum sweep, which is the more expensive of the two on
 * the server: it counts and hashes every subscription the reader holds.
 *
 * It ran on *every* convergence, so a shop with four terminals open paid for
 * thirteen aggregate queries per terminal per page refresh. Worse, a table that
 * genuinely had drifted triggered `runOfflineListSync` from the heal branch
 * below on every one of those refreshes, straight past the watermark floor —
 * the throttle was in place and the check that bypassed it was not.
 */
export const CHECKSUM_FLOOR_MS = 6 * 60 * 60_000;

/**
 * Where both floors are remembered between page loads.
 *
 * They used to live in module scope alone, which meant a hard refresh reset them
 * to zero and every refresh re-ran the whole thirteen-table scan and the
 * checksum sweep. That single line was the largest part of "the app hammers the
 * server every time I press F5": the throttles were real, they just could not
 * survive the event they most needed to survive.
 *
 * `offline_sync_meta` is declared `'key, table, scope, last_success_at'` — `key`
 * is the primary key and the rest are indexes — so two unindexed fields on one
 * row need no Dexie version bump. `table: '__convergence__'` keeps the row out
 * of `invalidateBranchStockCaches`, which sweeps by `table === 'branch_products'`.
 */
const CONVERGENCE_META_KEY = '__convergence__:global';

let lastWatermarkScanAt = 0;
let lastChecksumVerifyAt = 0;
let floorsHydrated = false;
let convergenceInFlight = null;

/** Test seam: the floor is time-based and a test cannot wait six hours. */
export function __setLastWatermarkScanAt(at = 0) {
    lastWatermarkScanAt = at;
    floorsHydrated = true;
}

/**
 * Test seam: every piece of throttle state this module keeps at module scope.
 *
 * All of it survives an individual test, and all of it decides whether work
 * happens — so a test that clears only some of it silently measures the leftover
 * rather than the behaviour it names.
 */
export function __resetConvergenceThrottle() {
    convergenceInFlight = null;
    lastWatermarkScanAt = 0;
    lastChecksumVerifyAt = 0;
    floorsHydrated = false;
}

/**
 * Load both floors from the last page load, once per session.
 *
 * Stamps only ever move *forward*: a reseed inside this run may already have set
 * one for this tick, and a stored value from an hour ago must not undo it.
 */
async function hydrateFloors() {
    if (floorsHydrated) return;
    floorsHydrated = true;

    try {
        const row = await offlineDB.offline_sync_meta?.get(CONVERGENCE_META_KEY);
        const scannedAt = Date.parse(row?.last_watermark_scan_at ?? '');
        const verifiedAt = Date.parse(row?.last_checksum_verify_at ?? '');
        if (!Number.isNaN(scannedAt) && scannedAt > lastWatermarkScanAt) lastWatermarkScanAt = scannedAt;
        if (!Number.isNaN(verifiedAt) && verifiedAt > lastChecksumVerifyAt) lastChecksumVerifyAt = verifiedAt;
    } catch (e) {
        // A floor we could not read reads as "never ran", which costs one extra
        // scan rather than skipping one that was owed.
        console.warn('[offlineSync] could not read convergence floors', e);
    }
}

async function persistFloors() {
    try {
        if (!offlineDB.offline_sync_meta) return;
        await offlineDB.offline_sync_meta.put({
            key: CONVERGENCE_META_KEY,
            table: '__convergence__',
            scope: 'global',
            last_watermark_scan_at: lastWatermarkScanAt ? new Date(lastWatermarkScanAt).toISOString() : null,
            last_checksum_verify_at: lastChecksumVerifyAt ? new Date(lastChecksumVerifyAt).toISOString() : null,
        });
    } catch (e) {
        console.warn('[offlineSync] could not persist convergence floors', e);
    }
}

/**
 * **The watermark scan is a floor beneath the replay, never an alternative to
 * it.**
 *
 * That distinction is the whole reason the server is allowed to write the change
 * feed best-effort (`OfflineSyncJob::recordSyncChange`). Both directions of feed
 * failure then cost latency rather than correctness: a row logged for a mutation
 * that rolled back costs one idempotent re-read, and a mutation that never got
 * logged is picked up by the next scan.
 *
 * Letting a successful replay stand in for the scan is what removes that
 * property. The replay cannot know what it was never told, so a dropped feed row
 * would be invisible to every later poll and the cache would sit confidently
 * wrong until someone cleared it by hand. Skipping the scan because it ran
 * *recently* is fine — that bounds staleness at WATERMARK_FLOOR_MS. Skipping it
 * because the replay worked bounds nothing at all.
 */
export async function convergeOfflineCaches({ reason = 'manual', force = false } = {}) {
    /*
     * Single-flight, and deliberately nothing more.
     *
     * Three independent triggers reach this — login, the offline→online edge and
     * a tab becoming visible — and none of them knew about the others, so two
     * firing together ran two thirteen-table fan-outs against each other. Joining
     * the in-flight run is what makes "sync resumes exactly once" true.
     *
     * A minimum gap *between* runs was tried here and removed: the expensive half
     * is already bounded by WATERMARK_FLOOR_MS below, so a time gate at this
     * level adds nothing to it — while suppressing the cheap half, the change-feed
     * replay, which is precisely what a reconnect shortly after a login needs in
     * order to catch up the events it missed. The storm this was reaching for had
     * its real cause upstream anyway: a fabricated offline→online edge on every
     * recovered blip, now fixed in onlineCheckService by deriving the edge from
     * confirmed state instead of trusting a caller-supplied one.
     */
    if (convergenceInFlight) return convergenceInFlight;

    convergenceInFlight = runConvergence({ reason, force }).finally(() => {
        convergenceInFlight = null;
    });

    return convergenceInFlight;
}

async function runConvergence({ reason, force }) {
    // Owned by this function rather than left to runOfflineListSync, which the
    // replay path below can legitimately skip. Reverb does not replay, so every
    // branch_products TTL marker that survives an outage would let the next POS
    // read skip its refetch — and that is true of the boundary itself, not of
    // whichever convergence path happens to run after it. Markers only; cached
    // rows and cart reservations survive, and a second call is a no-op.
    await invalidateBranchStockCaches();
    await hydrateFloors();

    const replayed = await runChangeFeedSync({ reason });

    // A reseed already ran mode:'full' inside the replay; running the scan again
    // here would double the cost of the one path that is already the expensive
    // one.
    if (replayed?.ok && replayed.reseeded) lastWatermarkScanAt = Date.now();

    // A replay that was never attempted is not a replay that failed.
    //
    // Two ways that happens, and neither is news: the feed is switched off in
    // this install, or it is inside the retry window a previous failure earned
    // it. Both used to satisfy `!replayed?.ok` and escalate straight to the scan,
    // which is how a *disabled feature* and a *silent backoff* each ended up
    // dragging thirteen paginated reads behind every refresh.
    //
    // The escalation itself stays, and matters: when a replay genuinely fails,
    // it cannot report what moved and the scan is the only thing that can, so it
    // runs past its floor. That is the composition rule this module rests on.
    // The failure that armed the ladder escalates once; the silence after it is
    // not a second piece of evidence.
    const replayUnavailable = replayed?.reason === 'flag_disabled' || replayed?.reason === 'backoff';
    const replayFailed = replayed?.ok !== true && !replayUnavailable;
    const floorMs = replayed?.ok === true ? WATERMARK_FLOOR_MS : DEGRADED_WATERMARK_FLOOR_MS;
    const scanIsDue = force || replayFailed || replayed?.truncated || Date.now() - lastWatermarkScanAt >= floorMs;

    let converged = replayed;
    if (scanIsDue) {
        // `force` also reaches the scan itself, so a manual refresh ignores the
        // per-table retry windows rather than quietly skipping the very tables
        // whose failure prompted the operator to press the button.
        converged = await runOfflineListSync({ reason, force });
        if (converged?.ok) lastWatermarkScanAt = Date.now();
    }

    // Throttled independently of the scan, and for a different reason: the scan
    // is skipped when nothing is likely to have gone stale, the sweep is skipped
    // because asking the server to hash every subscription is the most expensive
    // question this module knows how to ask. `force` — the Settings refresh
    // button — bypasses both, because an operator pressing it is new information.
    const verifyIsDue = force || Date.now() - lastChecksumVerifyAt >= CHECKSUM_FLOOR_MS;
    try {
        const verified = verifyIsDue ? await verifyOfflineCaches({ reason }) : { ok: false, drifted: [] };
        if (verified.ok) lastChecksumVerifyAt = Date.now();
        if (verified.ok && verified.drifted.length) {
            // Two unrelated failures wear the same checksum mismatch and the
            // repair for one cannot fix the other. A row that left this reader's
            // scope has to be evicted — no delete was ever written for it. A row
            // still legitimately held but stale has to be re-read, and eviction
            // will not touch it because the manifest still lists it. Without the
            // scan here, that second kind of drift was detected on every
            // convergence and repaired on none of them.
            //
            // Not forced, deliberately. Drift is proof the cache is wrong, which
            // is an argument for repairing it now — but a drifted table is most
            // often a *failing* table, and forcing here would bypass its retry
            // backoff on every single convergence, turning one broken endpoint
            // back into the request loop the backoff exists to prevent. The
            // eviction below still runs immediately, and the re-read lands as
            // soon as the window opens.
            const healed = await runOfflineListSync({ reason: `drift:${reason}` });
            if (healed?.ok) lastWatermarkScanAt = Date.now();
            await reconcileSubscriptions({ reason, subscriptions: verified.drifted });
        }
    } catch (e) {
        // Verification is a safety net, never a gate: a failed check must not
        // discard a convergence that already succeeded.
        console.warn('[offlineSync] verification failed', e);
    }

    // After the work, not before it: a stamp written up front would let a run
    // that then failed suppress the retry that failure is supposed to earn.
    await persistFloors();

    return converged;
}

export default { convergeOfflineCaches, WATERMARK_FLOOR_MS };
